import { knowledgeApi } from '../../supportApi';
import { resolveClubDisplayLabel } from '../lib/discussionUtils';

const STORAGE_PREFIX = 'od_posts_view_later_v1';
const MAX_ITEMS = 50;

export function viewLaterStorageKey(userId) {
  const id = String(userId || '').trim();
  return `${STORAGE_PREFIX}:${id || 'anonymous'}`;
}

export function loadViewLaterItems(userId) {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(viewLaterStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item) => item?.slug) : [];
  } catch {
    return [];
  }
}

export function saveViewLaterItems(userId, items) {
  if (typeof window === 'undefined') return;
  try {
    const list = Array.isArray(items) ? items.slice(0, MAX_ITEMS) : [];
    localStorage.setItem(viewLaterStorageKey(userId), JSON.stringify(list));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Snapshot thread fields for sidebar + restore. */
export function threadToViewLaterItem(thread) {
  if (!thread?.slug) return null;
  const slug = String(thread.slug).trim();
  if (!slug) return null;
  return {
    slug,
    title: String(thread.title || '').trim() || 'Untitled post',
    clubLabel: String(
      resolveClubDisplayLabel(thread.categoryKey || thread.clubSlug, thread.clubLabel || thread.categoryLabel) ||
        'Club'
    ).trim(),
    categoryKey: String(thread.categoryKey || thread.clubSlug || '').trim(),
    clubThumbnailUrl: thread.clubThumbnailUrl || '',
    commentCount: Number(thread.commentCount) || 0,
    voteScore: Number(thread.voteScore) || 0,
    updatedAt: thread.updatedAt || thread.createdAt || '',
    savedAt: new Date().toISOString(),
  };
}

export function isThreadInViewLater(slug, items) {
  const s = String(slug || '').trim();
  if (!s || !Array.isArray(items)) return false;
  return items.some((item) => String(item?.slug || '').trim() === s);
}

export function upsertViewLaterItem(items, nextItem) {
  if (!nextItem?.slug) return items;
  const slug = String(nextItem.slug).trim();
  const without = (Array.isArray(items) ? items : []).filter((item) => String(item?.slug || '').trim() !== slug);
  return [nextItem, ...without].slice(0, MAX_ITEMS);
}

export function removeViewLaterItem(items, slug) {
  const s = String(slug || '').trim();
  if (!s) return items;
  return (Array.isArray(items) ? items : []).filter((item) => String(item?.slug || '').trim() !== s);
}

/** Minimal list-row thread when full fetch is unavailable. */
export function viewLaterItemToListThread(item) {
  if (!item?.slug) return null;
  const slug = String(item.slug).trim();
  const categoryKey = String(item.categoryKey || '').trim();
  return {
    slug,
    title: String(item.title || '').trim() || 'Untitled post',
    body: '',
    categoryKey,
    clubSlug: categoryKey,
    clubLabel: String(item.clubLabel || 'Club').trim(),
    categoryLabel: String(item.clubLabel || 'Club').trim(),
    clubThumbnailUrl: item.clubThumbnailUrl || '',
    commentCount: Number(item.commentCount) || 0,
    voteScore: Number(item.voteScore) || 0,
    updatedAt: item.updatedAt || item.savedAt || '',
    createdAt: item.updatedAt || item.savedAt || '',
  };
}

/** Full threads for list view; falls back to per-slug get or snapshot rows. */
export async function fetchViewLaterThreadsForDisplay(viewLaterItems) {
  const items = Array.isArray(viewLaterItems) ? viewLaterItems.filter((item) => item?.slug) : [];
  if (!items.length) return [];

  let threads = [];
  try {
    const data = await knowledgeApi.discussionsViewLaterThreads();
    threads = Array.isArray(data?.threads) ? data.threads.filter((t) => t?.slug) : [];
  } catch {
    threads = [];
  }

  const bySlug = new Map(threads.map((t) => [String(t.slug).trim(), t]));
  const missing = items.filter((item) => !bySlug.has(String(item.slug).trim()));

  if (missing.length) {
    const results = await Promise.allSettled(
      missing.map((item) => knowledgeApi.discussionsGet(String(item.slug).trim()))
    );
    results.forEach((result, idx) => {
      const item = missing[idx];
      const slug = String(item.slug).trim();
      if (result.status === 'fulfilled' && result.value?.thread) {
        bySlug.set(slug, result.value.thread);
        return;
      }
      const stub = viewLaterItemToListThread(item);
      if (stub) bySlug.set(slug, stub);
    });
  }

  return items
    .map((item) => bySlug.get(String(item.slug).trim()))
    .filter((t) => t?.slug);
}
